import { httpClient, unwrap, unwrapPaginated } from '@/services/httpClient';

export interface GeneralSettings {
  app_name: string;
  default_language: string;
  default_timezone: string;
  allow_registration: boolean;
  session_timeout_minutes: number;
  google_oauth_enabled: boolean;
}

export interface SmtpSettings {
  configured?: boolean;
  host?: string;
  port?: number;
  username?: string;
  encryption?: string;
  from_email?: string;
  from_name?: string;
  is_verified?: boolean;
}

export interface SmtpPayload {
  host: string;
  port?: number;
  username?: string;
  password?: string;
  encryption?: 'tls' | 'ssl' | 'none';
  from_email: string;
  from_name?: string;
}

export interface BrandingSettings {
  configured?: boolean;
  logo_url?: string | null;
  logo_dark_url?: string | null;
  favicon_url?: string | null;
  brand_color?: string | null;
  custom_domain?: string | null;
  email_footer_text?: string | null;
}

export interface LanguageOption {
  id: number;
  organization_id: number | null;
  language_code: string;
  language_name: string;
  is_default: boolean;
  is_enabled: boolean;
}

export interface TimezoneOption {
  id: number;
  organization_id: number | null;
  timezone_name: string;
  utc_offset_minutes: number;
  is_default: boolean;
}

export interface CurrencyOption {
  id: number;
  organization_id: number | null;
  currency_code: string;
  currency_name: string;
  symbol: string;
  is_default: boolean;
  is_enabled: boolean;
}

export interface ThemeSettings {
  mode: 'light' | 'dark' | 'system';
  primary_color: string | null;
  accent_color: string | null;
  font_family: string | null;
  custom_css: string | null;
}

export interface SecuritySettings {
  min_password_length: number;
  require_uppercase: boolean;
  require_number: boolean;
  require_symbol: boolean;
  password_expiry_days: number | null;
  max_login_attempts: number;
  lockout_duration_minutes: number;
}

export interface ApiKey {
  id: number;
  name: string;
  key_prefix: string;
  scopes: string[] | null;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface BackupSettings {
  organization_id: number;
  frequency: 'manual' | 'daily' | 'weekly' | 'monthly';
  retention_days: number;
  last_backup_at: string | null;
  last_backup_status: 'succeeded' | 'failed' | null;
}

export interface BackupRun {
  id: number;
  file_size_bytes: number | null;
  run_status: 'running' | 'succeeded' | 'failed';
  error_message: string | null;
  started_at: string;
  completed_at: string | null;
}

export interface TwoFactorStatus {
  enabled: boolean;
  has_pending_secret: boolean;
}

export const settingsApi = {
  getGeneral: () => unwrap<GeneralSettings>(httpClient.get('/settings/general')),
  updateGeneral: (payload: Partial<GeneralSettings>) => unwrap<GeneralSettings>(httpClient.put('/settings/general', payload)),

  getSmtp: () => unwrap<SmtpSettings>(httpClient.get('/settings/smtp')),
  updateSmtp: (payload: SmtpPayload) => unwrap<SmtpSettings>(httpClient.put('/settings/smtp', payload)),

  getBranding: () => unwrap<BrandingSettings>(httpClient.get('/settings/branding')),
  updateBranding: (payload: Partial<BrandingSettings>) => unwrap<BrandingSettings>(httpClient.put('/settings/branding', payload)),
  uploadBrandingAsset: (type: 'logo' | 'logo-dark' | 'favicon', file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return unwrap<Record<string, string>>(
      httpClient.post(`/settings/branding/${type}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    );
  },

  getTheme: () => unwrap<ThemeSettings>(httpClient.get('/settings/theme')),
  updateTheme: (payload: Partial<ThemeSettings>) => unwrap<ThemeSettings>(httpClient.put('/settings/theme', payload)),

  getLanguages: () => unwrap<LanguageOption[]>(httpClient.get('/settings/languages')),
  addLanguage: (languageCode: string, languageName: string) =>
    unwrap<{ id: number }>(httpClient.post('/settings/languages', { language_code: languageCode, language_name: languageName })),
  removeLanguage: (id: number) => unwrap<null>(httpClient.delete(`/settings/languages/${id}`)),
  setDefaultLanguage: (id: number) => unwrap<LanguageOption[]>(httpClient.put(`/settings/languages/${id}/default`)),

  getTimezones: () => unwrap<TimezoneOption[]>(httpClient.get('/settings/timezones')),
  addTimezone: (timezoneName: string, utcOffsetMinutes: number) =>
    unwrap<{ id: number }>(httpClient.post('/settings/timezones', { timezone_name: timezoneName, utc_offset_minutes: utcOffsetMinutes })),
  removeTimezone: (id: number) => unwrap<null>(httpClient.delete(`/settings/timezones/${id}`)),
  setDefaultTimezone: (id: number) => unwrap<TimezoneOption[]>(httpClient.put(`/settings/timezones/${id}/default`)),

  getCurrencies: () => unwrap<CurrencyOption[]>(httpClient.get('/settings/currencies')),
  addCurrency: (currencyCode: string, currencyName: string, symbol: string) =>
    unwrap<{ id: number }>(httpClient.post('/settings/currencies', { currency_code: currencyCode, currency_name: currencyName, symbol })),
  removeCurrency: (id: number) => unwrap<null>(httpClient.delete(`/settings/currencies/${id}`)),
  setDefaultCurrency: (id: number) => unwrap<CurrencyOption[]>(httpClient.put(`/settings/currencies/${id}/default`)),

  getSecurity: () => unwrap<SecuritySettings>(httpClient.get('/settings/security')),
  updateSecurity: (payload: Partial<SecuritySettings>) => unwrap<SecuritySettings>(httpClient.put('/settings/security', payload)),

  getApiKeys: () => unwrap<ApiKey[]>(httpClient.get('/settings/api-keys')),
  createApiKey: (name: string, expiresAt?: string) =>
    unwrap<{ id: number; key: string }>(httpClient.post('/settings/api-keys', { name, expires_at: expiresAt })),
  revokeApiKey: (id: number) => unwrap<null>(httpClient.delete(`/settings/api-keys/${id}`)),

  getBackupSettings: () => unwrap<BackupSettings>(httpClient.get('/settings/backup')),
  updateBackupSettings: (payload: Partial<Pick<BackupSettings, 'frequency' | 'retention_days'>>) =>
    unwrap<BackupSettings>(httpClient.put('/settings/backup', payload)),
  runBackupNow: () => unwrap<BackupRun>(httpClient.post('/settings/backup/run')),
  backupHistory: (page: number) => unwrapPaginated<BackupRun>(httpClient.get('/settings/backup/history', { params: { page, per_page: 10 } })),
  downloadBackup: async (id: number): Promise<string> => {
    const response = await httpClient.get(`/settings/backup/${id}/download`, { responseType: 'blob' });
    return URL.createObjectURL(response.data as Blob);
  },

  getTwoFactorStatus: () => unwrap<TwoFactorStatus>(httpClient.get('/users/me/two-factor')),
  setupTwoFactor: () => unwrap<{ secret: string; otpauth_uri: string }>(httpClient.post('/users/me/two-factor/setup')),
  verifyTwoFactor: (code: string) => unwrap<{ enabled: boolean }>(httpClient.post('/users/me/two-factor/verify', { code })),
  disableTwoFactor: (currentPassword: string) =>
    unwrap<{ enabled: boolean }>(httpClient.post('/users/me/two-factor/disable', { current_password: currentPassword })),
};
